import { Link } from 'react-router'
import { Button } from '@/components/ui/button'
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger
} from '@/components/ui/drawer'
import { DotsHorizontalIcon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import {
	type CheckInAction,
	type CheckInActionId,
	checkInActionToLabelKey,
	segmentLabel
} from '@/features/marquee/utils'
import { cn } from '@/helpers/utils'
import type { MarqueeAttendee } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

/** Subtitle line for the sheet header — company · segment. */
const sheetSubtitle = (attendee: MarqueeAttendee) =>
	[attendee.company, attendee.segment && segmentLabel(attendee.segment)]
		.filter(Boolean)
		.join(' · ')

interface CheckInRowSheetProps {
	attendee: MarqueeAttendee
	/**
	 * The row's secondary actions, already filtered to the ones the event's state
	 * allows. The caller owns that filtering, so this sheet never offers an action
	 * the backend would refuse.
	 */
	actions: CheckInAction[]
	/**
	 * Attendee-detail route, when there is one. Only a tune-up has the page, so
	 * on a presentation the "See details" item is simply absent.
	 */
	detailPath?: string
	/** Dispatches a contextual action for this row (see the page dispatcher). */
	onAction: (attendee: MarqueeAttendee, action: CheckInActionId) => void
	/** Which action is in flight for this row, so the spinner lands correctly. */
	pending: CheckInActionId | null
	/** Open state, owned by the list, for the same reason the kebab's is. */
	open: boolean
	onOpenChange: (open: boolean) => void
}

/**
 * Mobile "⋯" kebab → bottom sheet listing the row's secondary actions for its
 * state. The primary check-in/undo stays on the checkbox, so "clean" states
 * (arrived / walk-in with host-id) show an explicit empty message instead,
 * matching the action matrix. Layout mirrors Figma 268-9797: centred
 * title/subtitle over stacked full-width action buttons.
 */
export function CheckInRowSheet({
	actions,
	attendee,
	detailPath,
	onAction,
	pending,
	open,
	onOpenChange
}: CheckInRowSheetProps) {
	const { t } = useTranslation()

	// The sheet stays up while the write runs, so the button the operator tapped
	// is what reports the wait. Dismissing on tap, as this used to, left the row
	// looking untouched until the toast arrived.
	return (
		<Drawer onOpenChange={onOpenChange} open={open}>
			<DrawerTrigger
				aria-label={t('marquee:checkin_row_menu_label')}
				className='group -my-1 flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full text-neutral-500 transition hover:text-neutral-900 data-[state=open]:text-neutral-900'
				type='button'
			>
				<span className='flex size-8 items-center justify-center rounded-full transition group-hover:bg-grey-100 group-data-[state=open]:bg-grey-100'>
					<DotsHorizontalIcon className='size-5' />
				</span>
			</DrawerTrigger>
			<DrawerContent>
				<DrawerHeader className='pb-4'>
					<DrawerTitle className='font-semibold text-neutral-900 text-xl'>
						{attendee.name || '-'}
					</DrawerTitle>
					<DrawerDescription className='text-neutral-500'>
						{sheetSubtitle(attendee)}
					</DrawerDescription>
				</DrawerHeader>

				{actions.length === 0 && !detailPath ? (
					<p className='whitespace-pre-line px-4 pb-2 text-center text-neutral-500 text-sm'>
						{t('marquee:checkin_sheet_empty')}
					</p>
				) : (
					<DrawerFooter>
						{actions.map((action) => (
							<Button
								className={cn(
									'w-full justify-center gap-2',
									action.tone === 'danger' &&
										'text-error-600 hover:text-error-700'
								)}
								disabled={pending !== null}
								key={action.id}
								onClick={() => onAction(attendee, action.id)}
								size='lg'
								type='button'
								variant='secondary'
							>
								{t(checkInActionToLabelKey[action.id])}
								{pending === action.id && <Spinner size={16} />}
							</Button>
						))}
						{detailPath && (
							<Button
								asChild
								className='w-full justify-center'
								size='lg'
								variant='secondary'
							>
								<Link to={detailPath}>
									{t('marquee:checkin_action_see_details')}
								</Link>
							</Button>
						)}
					</DrawerFooter>
				)}

				<div
					className={cn(
						'px-4 pb-4',
						actions.length === 0 && !detailPath && 'pt-2'
					)}
				>
					<DrawerClose asChild>
						<Button
							className='w-full justify-center'
							size='lg'
							type='button'
							variant='tertiary'
						>
							{t('marquee:cancel')}
						</Button>
					</DrawerClose>
				</div>
			</DrawerContent>
		</Drawer>
	)
}
